import { InviteKey, ItemId, LiveId, SelectionId, SpaceKey, UserId, ShopCustomTagId, BrandId, SaleId, SystemNotificationId, PersonalNotificationId, StampCardId } from "popopo-msg"
import { publicRouteDefinitions, getLocalePath, buildLocalePath, Locale } from "popopo-msg/routes"

export type { Locale } from "popopo-msg/routes"

const r = publicRouteDefinitions

const debugRoutes = {
  top: {
    path: "/debug",
  },

  home: {
    path: "/debug/home",
  },

  notifications: {
    path: "/debug/notifications",
  },

  shop: {
    path: "/debug/shop",
  },

  my: {
    path: "/debug/my",
  },

  items: {
    path: "/debug/items",

    item: {
      path: "/debug/items/:itemId",
      getPath: (itemId: ItemId) => `/debug/items/${itemId}`,
      params: {
        itemId: ItemId,
      },
    },
  },

  users: {
    user: {
      path: "/debug/users/:userId",
      getPath: (userId: UserId) => `/debug/users/${userId}`,
      params: {
        userId: UserId,
      },
    },
  },

  spaces: {
    space: {
      path: "/debug/spaces/:spaceKey",
      getPath: (spaceKey: SpaceKey) => `/debug/spaces/${spaceKey}`,
      params: {
        spaceKey: SpaceKey,
      },

      lives: {
        live: {
          path: "/debug/spaces/:spaceKey/lives/:liveId",
          getPath: (spaceKey: SpaceKey, liveId: LiveId) => `/debug/spaces/${spaceKey}/lives/${liveId}`,
          params: {
            spaceKey: SpaceKey,
            liveId: LiveId,
          },

          selections: {
            selection: {
              path: "/debug/spaces/:spaceKey/lives/:liveId/selections/:selectionId",
              getPath: (spaceKey: SpaceKey, liveId: LiveId, selectionId: SelectionId) => 
                `/debug/spaces/${spaceKey}/lives/${liveId}/selections/${selectionId}`,
              params: {
                spaceKey: SpaceKey,
                liveId: LiveId,
                selectionId: SelectionId,
              },
            },
          },
        },
      },
    },
  },

  oauth: {
    virtualcast: {
      callback: {
        path: "/oauth/virtualcast/callback",
      },
    },
  },

  trtcTester: {
    path: "/debug/trtc-tester",
  },

  stories: {
    path: "/debug/stories",
  },

  voices: {
    path: "/debug/voices",
  },

  recommendedUsers: {
    path: "/debug/recommended-users",
  },

  violationReport: {
    path: "/debug/violation-report",
  },

  error: {
    path: "/debug/error",
  },

  dotMoney: {
    path: "/debug/dot-money",
  },
}


function publicRoutes(locale: Locale) {
  return {
    users: {
      user: {
        path: getLocalePath(locale, r.users.user.pattern),
        getPath: (userId: UserId) => buildLocalePath(locale, r.users.user.pattern, { userId }),
        params: {
          userId: UserId,
        },

        follow: {
          path: getLocalePath(locale, r.users.follow.pattern),
          getPath: (userId: UserId) => buildLocalePath(locale, r.users.follow.pattern, { userId }),
          params: {
            userId: UserId,
          },
        },
      },
    },

    spaces: {
      space: {
        path: getLocalePath(locale, r.spaces.space.pattern),
        getPath: (spaceKey: SpaceKey) => buildLocalePath(locale, r.spaces.space.pattern, { spaceKey }),
        params: {
          spaceKey: SpaceKey,
        },

        lives: {
          live: {
            path: getLocalePath(locale, r.spaces.live.pattern),
            getPath: (spaceKey: SpaceKey, liveId: LiveId) => buildLocalePath(locale, r.spaces.live.pattern, { spaceKey, liveId }),
            params: {
              spaceKey: SpaceKey,
              liveId: LiveId,
            },
          },
        },
      },
    },

    shop: {
      path: getLocalePath(locale, r.shop.top.pattern),
      getPath: () => getLocalePath(locale, r.shop.top.pattern),

      search: {
        path: getLocalePath(locale, r.shop.search.pattern),
        getPath: () => getLocalePath(locale, r.shop.search.pattern),
      },
      items: {
        item: {
          path: getLocalePath(locale, r.shop.item.pattern),
          getPath: (itemId: ItemId) => buildLocalePath(locale, r.shop.item.pattern, { itemId }),
          params: {
            itemId: ItemId,
          },
        },
      },
      customTags: {
        customTag: {
          path: getLocalePath(locale, r.shop.customTag.pattern),
          getPath: (customTagId: ShopCustomTagId) => buildLocalePath(locale, r.shop.customTag.pattern, { customTagId }),
          params: {
            customTagId: ShopCustomTagId,
          },
        },
      },
      brands: {
        brand: {
          path: getLocalePath(locale, r.shop.brand.pattern),
          getPath: (brandId: BrandId) => buildLocalePath(locale, r.shop.brand.pattern, { brandId }),
          params: {
            brandId: BrandId,
          },
        },
      },
      collections: {
        collection: {
          path: getLocalePath(locale, r.shop.collection.pattern),
          getPath: (collectionId: SaleId) => buildLocalePath(locale, r.shop.collection.pattern, { collectionId }),
          params: {
            collectionId: SaleId,
          },
        },
      },
    },

    invites: {
      friends: {
        path: getLocalePath(locale, r.invites.friends.pattern),
        getPath: (inviteKey: InviteKey) => buildLocalePath(locale, r.invites.friends.pattern, { inviteKey }),
        params: {
          inviteKey: InviteKey,
        },
      },
      spaces: {
        path: getLocalePath(locale, r.invites.spaces.pattern),
        getPath: (inviteKey: InviteKey) => buildLocalePath(locale, r.invites.spaces.pattern, { inviteKey }),
        params: {
          inviteKey: InviteKey,
        },
      },
    },

    premium: {
      path: getLocalePath(locale, r.premium.pattern),
      getPath: () => getLocalePath(locale, r.premium.pattern),
    },

    notifications: {
      system: {
        path: getLocalePath(locale, r.notifications.system.pattern),
        getPath: () => getLocalePath(locale, r.notifications.system.pattern),

        notification: {
          path: getLocalePath(locale, r.notifications.systemNotification.pattern),
          getPath: (notificationId: SystemNotificationId) => buildLocalePath(locale, r.notifications.systemNotification.pattern, { notificationId }),
          params: {
            notificationId: SystemNotificationId,
          },
        },
      },
      personal: {
        path: getLocalePath(locale, r.notifications.personal.pattern),
        getPath: () => getLocalePath(locale, r.notifications.personal.pattern),

        notification: {
          path: getLocalePath(locale, r.notifications.personalNotification.pattern),
          getPath: (notificationId: PersonalNotificationId) => buildLocalePath(locale, r.notifications.personalNotification.pattern, { notificationId }),
          params: {
            notificationId: PersonalNotificationId,
          },
        },
      },
    },

    top: {
      path: getLocalePath(locale, r.top.pattern),
      getPath: () => getLocalePath(locale, r.top.pattern),
    },

    my: {
      inventory: {
        path: getLocalePath(locale, r.my.inventory.pattern),
        getPath: () => getLocalePath(locale, r.my.inventory.pattern),
      },
      stampCards: {
        path: getLocalePath(locale, r.my.stampCards.pattern),
        getPath: () => getLocalePath(locale, r.my.stampCards.pattern),

        stampCard: {
          path: getLocalePath(locale, r.my.stampCard.pattern),
          getPath: (stampCardId: StampCardId) => buildLocalePath(locale, r.my.stampCard.pattern, { stampCardId }),
          params: {
            stampCardId: StampCardId,
          },
        },
      },
      histories: {
        path: getLocalePath(locale, r.my.histories.pattern),
        getPath: () => getLocalePath(locale, r.my.histories.pattern),
      },
      following: {
        path: getLocalePath(locale, r.my.following.pattern),
        getPath: () => getLocalePath(locale, r.my.following.pattern),
      },
      notifications: {
        path: getLocalePath(locale, r.my.notifications.pattern),
        getPath: () => getLocalePath(locale, r.my.notifications.pattern),
      },
    },

    news: {
      path: getLocalePath(locale, r.news.pattern),
      getPath: () => getLocalePath(locale, r.news.pattern),
    },

    about: {
      path: getLocalePath(locale, r.about.pattern),
      getPath: () => getLocalePath(locale, r.about.pattern),
    },

    contact: {
      path: getLocalePath(locale, r.contact.pattern),
      getPath: () => getLocalePath(locale, r.contact.pattern),
    },
  }
}

export class Paths {
  public static readonly debug = debugRoutes

  public static readonly ja = publicRoutes("ja")
  public static readonly en = publicRoutes("en")
  public static readonly ko = publicRoutes("ko")

  public static readonly top = Paths.debug.top
  public static readonly home = Paths.debug.home
  public static readonly notifications = Paths.debug.notifications
  public static readonly shop = Paths.debug.shop
  public static readonly my = Paths.debug.my
  public static readonly items = Paths.debug.items
  public static readonly users = Paths.debug.users
  public static readonly spaces = Paths.debug.spaces
  public static readonly oauth = Paths.debug.oauth
  public static readonly trtcTester = Paths.debug.trtcTester
  public static readonly stories = Paths.debug.stories
  public static readonly voices = Paths.debug.voices
  public static readonly recommendedUsers = Paths.debug.recommendedUsers
  public static readonly violationReport = Paths.debug.violationReport
}
